import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Search as SearchIcon, X, Layers } from 'lucide-react';
import { search } from '../api/hub';
import { Logo } from '../components/Logo';
import { imageUrl } from '../api/client';
import { timeAgo, formatAbsoluteDate } from '../utils/timeAgo';
import type { SearchResult, SearchImageResult } from '../api/setsTypes';

// Sökningssida — autofokuserad input, blandat resultat (shoots + sets).
// Triggas via Cmd+K från Hub eller via /search-route.
export default function Search() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(params.get('q') ?? '');

  // Debounce queryn — undvik att skicka API-anrop per keystroke
  const [debounced, setDebounced] = useState(query);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Sync till URL för delbara länkar
  useEffect(() => {
    if (debounced) {
      setParams({ q: debounced }, { replace: true });
    } else {
      setParams({}, { replace: true });
    }
  }, [debounced, setParams]);

  // Autofocus + Esc → tillbaka
  useEffect(() => {
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  const resultsQuery = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => search(debounced),
    enabled: debounced.trim().length > 0,
  });

  const results = resultsQuery.data?.results ?? [];
  const imageResults = resultsQuery.data?.images ?? [];

  // Gruppera per typ för rendering
  const { sets, shoots } = useMemo(() => {
    const sets: SearchResult[] = [];
    const shoots: SearchResult[] = [];
    for (const r of results) {
      if (r.type === 'set') sets.push(r);
      else shoots.push(r);
    }
    return { sets, shoots };
  }, [results]);

  const totalResults = results.length + imageResults.length;

  const hasQuery = debounced.trim().length > 0;

  return (
    <div className="min-h-screen min-h-[100dvh]">
      <nav className="sticky top-0 z-50 flex items-center justify-between px-4 sm:px-7 py-3 bg-loupe-bg/85 backdrop-blur-xl border-b border-loupe-border h-[58px]">
        <div className="flex items-center gap-3 sm:gap-5 min-w-0">
          <Link
            to="/"
            className="text-loupe-text-3 hover:text-loupe-text text-xs uppercase tracking-[0.1em] py-2 inline-flex items-center gap-1.5"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Back</span>
          </Link>
          <Logo size="sm" color="#ededed" />
        </div>
      </nav>

      <div className="max-w-[1200px] mx-auto px-5 sm:px-10 pt-12 sm:pt-16 pb-32">
        {/* Big search input */}
        <div className="relative mb-2">
          <SearchIcon className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-loupe-text-3 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search shoots, sets, tags…"
            className="w-full pl-14 pr-14 py-4 sm:py-5 bg-loupe-bg-2 border border-loupe-border-2 rounded-2xl text-loupe-text font-display text-2xl sm:text-3xl font-light tracking-tight outline-none transition-all focus:border-henrik focus:bg-loupe-bg-3 focus:shadow-[0_0_0_4px_rgba(95,168,211,0.08)] placeholder:text-loupe-text-3 placeholder:italic"
          />
          {query ? (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              aria-label="Clear"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 w-8 h-8 bg-loupe-bg-3 border border-loupe-border-2 text-loupe-text-2 hover:text-loupe-text rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>

        <div className="px-2 mb-10 text-[11px] text-loupe-text-3 tracking-wider uppercase flex items-center justify-between gap-4">
          <span>
            {hasQuery
              ? resultsQuery.isLoading
                ? 'Searching…'
                : `${totalResults} ${totalResults === 1 ? 'result' : 'results'}`
              : 'Start typing to search'}
          </span>
          <span>⌘K · Esc</span>
        </div>

        {/* Results */}
        {!hasQuery ? (
          <div className="px-2 py-12 text-center text-loupe-text-3 text-sm">
            Search by name, description, or tag.
          </div>
        ) : resultsQuery.isLoading ? (
          <SearchSkeleton />
        ) : totalResults === 0 ? (
          <div className="rounded-2xl border border-dashed border-loupe-border-2 p-12 text-center">
            <div className="font-display text-2xl mb-2">No results</div>
            <p className="text-loupe-text-2 text-sm">
              Try a different search, or open the hub to browse all shoots and sets.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-10">
            {sets.length > 0 ? (
              <section>
                <div className="text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase mb-3.5 px-2">
                  Sets · {sets.length}
                </div>
                <div className="flex flex-col gap-2">
                  {sets.map((r) => (
                    <ResultRow key={`set-${r.id}`} result={r} query={debounced} />
                  ))}
                </div>
              </section>
            ) : null}

            {shoots.length > 0 ? (
              <section>
                <div className="text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase mb-3.5 px-2">
                  Shoots · {shoots.length}
                </div>
                <div className="flex flex-col gap-2">
                  {shoots.map((r) => (
                    <ResultRow key={`shoot-${r.id}`} result={r} query={debounced} />
                  ))}
                </div>
              </section>
            ) : null}

            {imageResults.length > 0 ? (
              <section>
                <div className="text-[11px] text-loupe-text-3 tracking-[0.15em] uppercase mb-3.5 px-2">
                  Images · {imageResults.length}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {imageResults.map((img) => (
                    <ImageHit key={img.id} img={img} query={debounced} />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Result row ──────────────────────────────────────────────────────

function ResultRow({ result, query }: { result: SearchResult; query: string }) {
  const isSet = result.type === 'set';
  const to = isSet ? `/sets/${result.id}` : `/shoots/${result.id}`;
  const accent = isSet ? result.color ?? '#5fa8d3' : '#5fa8d3';

  const subline = isSet
    ? `${result.imageCount} frames · ${timeAgo(result.lastActivityAt)}`
    : `${result.imageCount} frames · ${formatAbsoluteDate(result.shootDate)}`;

  return (
    <Link
      to={to}
      className="flex items-center gap-4 p-3 bg-loupe-bg-2 hover:bg-loupe-bg-3 border border-loupe-border hover:border-loupe-border-2 rounded-xl transition-colors group"
    >
      {/* Cover */}
      <div
        className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-loupe-bg-3"
        style={{ ['--set-accent' as string]: accent }}
      >
        {result.coverImageId ? (
          <img
            src={imageUrl(result.coverImageId, 'thumb')}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, transparent 60%), #1a1a1a` }}
          >
            <span className="font-display text-2xl text-white/30">
              {isSet ? '⊞' : '◫'}
            </span>
          </div>
        )}
        {/* Type indicator */}
        {isSet ? (
          <span
            className="absolute left-0 top-0 bottom-0 w-1"
            style={{ background: accent }}
          />
        ) : null}
      </div>

      {/* Meta */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 font-medium flex items-center gap-1">
            {isSet ? (
              <Layers className="w-3 h-3" />
            ) : (
              <span className="text-loupe-text-3">◫</span>
            )}
            {result.type}
          </span>
        </div>
        <div className="font-display text-lg sm:text-xl font-medium tracking-tight leading-tight truncate">
          <Highlight text={result.name} query={query} />
        </div>
        {isSet && result.description ? (
          <div className="text-xs text-loupe-text-2 truncate mt-0.5">
            <Highlight text={result.description} query={query} />
          </div>
        ) : null}
        <div className="text-[11px] text-loupe-text-3 tracking-wider tabular-nums mt-1">
          {subline}
        </div>
        {isSet && result.tags && result.tags.length > 0 ? (
          <div className="flex flex-wrap gap-1 mt-2">
            {result.tags.map((t) => (
              <TagChip key={t} tag={t} query={query} />
            ))}
          </div>
        ) : null}
      </div>
    </Link>
  );
}

// Tag-chip med highlight om matchen kommer från taggen.
function TagChip({ tag, query }: { tag: string; query: string }) {
  const match = query && tag.toLowerCase().includes(query.toLowerCase());
  return (
    <span
      className={[
        'text-[10px] px-1.5 py-0.5 rounded-full border',
        match
          ? 'bg-henrik/15 text-henrik border-henrik/30'
          : 'bg-loupe-bg-3 text-loupe-text-2 border-loupe-border',
      ].join(' ')}
    >
      {tag}
    </span>
  );
}

// Highlight matchande del av text. Vi använder inte regex (per global instruktion).
function Highlight({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const q = query.toLowerCase();
  const lower = text.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-henrik/25 text-henrik px-0.5 rounded-sm not-italic">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

// Image hit-kort (visas i Images-sektionen vid partiell filnamnsmatch)
function ImageHit({ img, query }: { img: SearchImageResult; query: string }) {
  const aspect = img.width && img.height ? `${img.width} / ${img.height}` : '2 / 3';
  return (
    <Link
      to={`/shoots/${img.shootId}/images/${img.id}`}
      className="group block relative rounded-md overflow-hidden bg-loupe-bg-2 border border-loupe-border hover:border-loupe-border-2 transition-colors"
    >
      <img
        src={imageUrl(img.id, 'thumb')}
        alt={img.filename}
        loading="lazy"
        className="w-full h-auto block group-hover:brightness-110 transition-[filter]"
        style={{ aspectRatio: aspect }}
      />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent p-2 pt-6">
        <div className="text-[10px] font-mono tabular-nums text-white/95 truncate">
          <Highlight text={img.filename} query={query} />
        </div>
        <div className="text-[9px] text-white/60 tracking-wide uppercase truncate">
          {img.shootName}
        </div>
      </div>
    </Link>
  );
}

function SearchSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 p-3 bg-loupe-bg-2 border border-loupe-border rounded-xl animate-pulse"
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-loupe-bg-3 flex-shrink-0" />
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="h-3 bg-loupe-bg-3 rounded w-1/4" />
            <div className="h-5 bg-loupe-bg-3 rounded w-2/3" />
            <div className="h-3 bg-loupe-bg-3 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}
